import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { changelogApi } from "../api/changelogApi";
import type {
  CreateChangelogEntryData,
  ListParams,
  UpdateChangelogEntryData,
} from "../types";

const LIST_KEY = "changelog-entries";
const DETAIL_KEY = "changelog-entry";

const invalidateAll = (
  queryClient: ReturnType<typeof useQueryClient>,
  id?: string,
) => {
  queryClient.invalidateQueries({ queryKey: [LIST_KEY] });
  if (id) {
    queryClient.invalidateQueries({ queryKey: [DETAIL_KEY, id] });
  }
};

export const useChangelogEntries = (params?: ListParams) => {
  return useQuery({
    queryKey: [LIST_KEY, params],
    queryFn: () => changelogApi.getEntries(params),
  });
};

export const useChangelogEntry = (id: string) => {
  return useQuery({
    queryKey: [DETAIL_KEY, id],
    queryFn: () => changelogApi.getEntry(id),
    enabled: !!id,
  });
};

export const useCreateChangelogEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateChangelogEntryData) =>
      changelogApi.createEntry(data),
    onSuccess: (entry) => invalidateAll(queryClient, entry.id),
  });
};

export const useUpdateChangelogEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateChangelogEntryData;
    }) => changelogApi.updateEntry(id, data),
    onSuccess: (entry) => invalidateAll(queryClient, entry.id),
  });
};

export const useDeleteChangelogEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => changelogApi.deleteEntry(id),
    onSuccess: (_, id) => invalidateAll(queryClient, id),
  });
};

export const usePublishChangelogEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => changelogApi.publishEntry(id),
    onSuccess: (entry) => invalidateAll(queryClient, entry.id),
  });
};

export const useUnpublishChangelogEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => changelogApi.unpublishEntry(id),
    onSuccess: (entry) => invalidateAll(queryClient, entry.id),
  });
};
